import { t } from '../i18n';
import notifications from '../redux/notifications/actions';

const HTTP_FORBIDDEN = 403;

export const PRESERVE_STATUS = { preserveErrorStatus: true };

type ValidationValue = string | string[] | { [key: string]: ValidationValue };

export type IValidations = Record<string, ValidationValue>;

export type IMutationResult =
  | {
      status?: number;
      validations?: IValidations;
      message?: string;
    }
  | undefined;

export function isMutationSuccess(result: IMutationResult): boolean {
  const status = result?.status;

  return status !== undefined && status >= 200 && status < 300;
}

function messageIn(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim() ? value : null;
  }

  if (Array.isArray(value)) {
    return value.reduce<string | null>((found, entry) => found ?? messageIn(entry), null);
  }

  if (value && typeof value === 'object') {
    return messageIn(Object.values(value));
  }

  return null;
}

export function validationMessageAt(validations: IValidations | null | undefined, key: string): string | null {
  return validations ? messageIn(validations[key]) : null;
}

export function firstValidationMessage(
  validations: IValidations | null | undefined,
  preferredKey?: string,
): string | null {
  if (!validations) {
    return null;
  }

  const preferred = preferredKey === undefined ? null : messageIn(validations[preferredKey]);

  return preferred ?? messageIn(Object.values(validations));
}

export function mutationSuccessNotification(messageKey: string, itemLabelKey: string) {
  return notifications.success(t(messageKey, { item: t(itemLabelKey) }));
}

export function mutationErrorNotification(message: string) {
  return notifications.addNotification({
    headline: t('dash.common.notification.error_headline'),
    type: 'error',
    message,
  });
}

export function mutationErrorMessage(result: NonNullable<IMutationResult>, fieldError?: string | null): string {
  const validationMessage = fieldError ?? firstValidationMessage(result.validations);

  if (validationMessage) {
    return validationMessage;
  }

  if (result.message) {
    return result.message;
  }

  return result.status === HTTP_FORBIDDEN
    ? t('dash.common.notification.forbidden_error')
    : t('dash.common.notification.check_validation_errors');
}
